import Editor, { type OnMount } from "@monaco-editor/react"
import { ArrowDown, ArrowUp, ImagePlus, Trash2 } from "lucide-react"
import { useRef, useState } from "react"

import CodeTakeCard, {
  SUPPORTED_CODE_LANGUAGES,
} from "@/components/code-take-card"
import MentionEditor from "@/components/mention-editor"
import MentionSuggestionTextarea from "@/components/mention-suggestion-textarea"
import PlatformLimitIndicator from "@/components/platform-limit-indicator"
import QuotedTakePicker from "@/components/stories/quoted-take-picker"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAppearance } from "@/hooks/use-appearance"
import { hasUnschemedUrl } from "@/lib/unschemed-url"
import type {
  CodeTake,
  MentionHandleGroup,
  Platform,
  QuotableTake,
  Take,
} from "@/types"

import MediaFileRow from "./media-file-row"
import {
  CODE_TAKE_TYPE,
  type StoryTakeAttribute,
  existingMediaCompositeKey,
  imageFilesFromClipboard,
  isCodeTake,
  isQuoteTake,
  mediaFileCompositeKey,
} from "./story-form-utils"
import { useTakeManagement } from "./use-take-management"

function isSavedCodeTake(take?: Take | CodeTake): take is CodeTake {
  return take?.type === CODE_TAKE_TYPE
}

const MONACO_LANGUAGE_MAP: Record<string, string> = {
  bash: "shell",
}

function toMonacoLanguage(lang: string): string {
  return MONACO_LANGUAGE_MAP[lang] ?? lang
}

interface Props {
  takeKey: number
  take: StoryTakeAttribute
  index: number
  totalTakes: number
  existingTake?: Take | CodeTake
  selectedPlatforms: Platform[]
  excludeStoryId?: number
  initialSelectedTake?: QuotableTake | null
  mentionHandleGroups: MentionHandleGroup[]
  mentionPlatforms: Platform[]
  mastodonServerDomain?: string | null
  onMentionGroupSaved: (group: MentionHandleGroup) => void
  onMove: (direction: "up" | "down") => void
  onRemove: () => void
  onUpdateBody: (body: string) => void
  onUpdateQuotedTake: (quotedTakeId: number | null) => void
  onUpdateCodeLanguage: (language: string) => void
  onUpdateCodeSnippet: (snippet: string) => void
  onHandleFiles: (
    files: File[],
    existingMediaFiles: {
      id: number
      content_type?: string | null
      url?: string | null
    }[],
    onLimitMessage: (message?: string) => void,
  ) => void
  onRemoveExistingMedia: (mediaId: number) => void
  onRemoveNewMedia: (mediaKey: number) => void
  onUpdateExistingAltText: (mediaId: number, altText: string) => void
  onUpdateNewAltText: (mediaKey: number, altText: string) => void
  onRegenerateExistingAltText: (mediaId: number, url: string) => void
  onRegenerateNewAltText: (mediaKey: number) => void
  generatingAltTextKeys: Set<string>
  codePreviewRef: (el: HTMLDivElement | null) => void
}

export default function TakeCard({
  takeKey,
  take,
  index,
  totalTakes,
  existingTake,
  selectedPlatforms,
  excludeStoryId,
  initialSelectedTake,
  mentionHandleGroups,
  mentionPlatforms,
  mastodonServerDomain,
  onMentionGroupSaved,
  onMove,
  onRemove,
  onUpdateBody,
  onUpdateQuotedTake,
  onUpdateCodeLanguage,
  onUpdateCodeSnippet,
  onHandleFiles,
  onRemoveExistingMedia,
  onRemoveNewMedia,
  onUpdateExistingAltText,
  onUpdateNewAltText,
  onRegenerateExistingAltText,
  onRegenerateNewAltText,
  generatingAltTextKeys,
  codePreviewRef,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [imageLimitMessage, setImageLimitMessage] = useState<
    string | undefined
  >(undefined)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [editorHeight, setEditorHeight] = useState(200)
  const { appearance } = useAppearance()
  const isDark =
    appearance === "dark" ||
    (appearance === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches)
  const monacoTheme = isDark ? "vs-dark" : "light"

  const handleEditorMount: OnMount = (editor) => {
    editor.onDidContentSizeChange(() => {
      setEditorHeight(Math.max(200, editor.getContentHeight()))
    })
    setEditorHeight(Math.max(200, editor.getContentHeight()))
  }

  const pendingDestroyIds = new Set(
    Object.values(take.take_media_files_attributes)
      .filter((attr) => attr._destroy)
      .map((attr) => attr.id),
  )
  const visibleExistingMedia = (existingTake?.media_files ?? []).filter(
    (mediaFile) => mediaFile.url && !pendingDestroyIds.has(mediaFile.id),
  )
  const newMediaFiles = Object.entries(take.take_media_files_attributes).filter(
    ([, attr]) => attr.media_file && !attr._destroy,
  )
  const mediaImageCount =
    visibleExistingMedia.filter((mediaFile) =>
      mediaFile.content_type?.startsWith("image/"),
    ).length +
    newMediaFiles.filter(([, attr]) =>
      attr.media_file?.type.startsWith("image/"),
    ).length

  return (
    <section
      className="flex flex-col gap-4 rounded-xl border p-4"
      data-testid={`take-card-${takeKey}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
            Take {index + 1}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={index === 0}
            onClick={() => onMove("up")}
          >
            <ArrowUp className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={index === totalTakes - 1}
            onClick={() => onMove("down")}
          >
            <ArrowDown className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={totalTakes === 1}
            onClick={onRemove}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor={`take-body-${takeKey}`}>Text</Label>
          <PlatformLimitIndicator
            length={take.body.length}
            platforms={selectedPlatforms}
          />
        </div>
        <MentionSuggestionTextarea
          id={`take-body-${takeKey}`}
          value={take.body}
          onValueChange={onUpdateBody}
          mentionHandleGroups={mentionHandleGroups}
          placeholder={
            index === 0 ? "What's the news?" : "Add the next take in the thread"
          }
          rows={5}
          className="resize-none font-serif text-base"
        />
        {hasUnschemedUrl(take.body) && (
          <p className="text-muted-foreground text-sm">
            Include <code>http://</code> or <code>https://</code> so URLs become
            clickable links.
          </p>
        )}
        <MentionEditor
          body={take.body}
          mentionHandleGroups={mentionHandleGroups}
          mentionPlatforms={mentionPlatforms}
          mastodonServerDomain={mastodonServerDomain}
          onMentionGroupSaved={onMentionGroupSaved}
        />
      </div>

      {/* Code take, quote take, or media */}
      {isCodeTake(take) ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor={`take-code-language-${takeKey}`}>Language</Label>
              <Select
                value={take.code_language}
                onValueChange={onUpdateCodeLanguage}
              >
                <SelectTrigger
                  id={`take-code-language-${takeKey}`}
                  className="w-full"
                >
                  <SelectValue placeholder="Select a language" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CODE_LANGUAGES.map(([language]) => (
                    <SelectItem key={language} value={language}>
                      {language}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor={`take-code-snippet-${takeKey}`}>
                Code snippet
              </Label>
              <div className="overflow-hidden rounded-md border">
                <Editor
                  language={toMonacoLanguage(take.code_language)}
                  value={take.code_snippet}
                  onChange={(value) => onUpdateCodeSnippet(value ?? "")}
                  theme={monacoTheme}
                  height={editorHeight}
                  width="100%"
                  onMount={handleEditorMount}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 13,
                    lineHeight: 20,
                    wordWrap: "on",
                    scrollbar: {
                      vertical: "hidden",
                      alwaysConsumeMouseWheel: false,
                    },
                    overviewRulerLanes: 0,
                  }}
                />
              </div>
            </div>
            <p className="text-muted-foreground text-sm">
              Broadside will render this preview into an image and attach it
              automatically when the story is saved and published.
            </p>
          </div>
          <div className="flex min-w-0 flex-col items-start gap-2 overflow-hidden">
            <Label>Preview</Label>
            <CodeTakeCard
              ref={codePreviewRef}
              codeSnippet={take.code_snippet}
              codeLanguage={take.code_language}
              compact
            />
            {isSavedCodeTake(existingTake) && existingTake.code_image_url && (
              <div className="flex flex-col gap-2 rounded-lg border p-3">
                <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
                  Saved image
                </p>
                <img
                  src={existingTake.code_image_url}
                  alt="Saved code take preview"
                  className="rounded-md border"
                />
              </div>
            )}
          </div>
        </div>
      ) : isQuoteTake(take) ? (
        <QuotedTakePicker
          takeKey={takeKey}
          value={take.quoted_take_id}
          excludeStoryId={excludeStoryId}
          initialSelectedTake={initialSelectedTake}
          selectedPlatforms={selectedPlatforms}
          onChange={onUpdateQuotedTake}
        />
      ) : (
        <div
          tabIndex={0}
          className={`focus-visible:ring-primary flex flex-col gap-2 rounded-lg p-2 transition-colors focus-visible:ring-2 focus-visible:outline-none ${
            isDraggingOver ? "ring-primary bg-primary/5 ring-2" : ""
          }`}
          onPaste={(e) => {
            const files = imageFilesFromClipboard(e.clipboardData)
            if (files.length === 0) return
            e.preventDefault()
            onHandleFiles(files, visibleExistingMedia, setImageLimitMessage)
          }}
          onDragEnter={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsDraggingOver(true)
          }}
          onDragOver={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsDraggingOver(true)
          }}
          onDragLeave={(e) => {
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
              return
            }
            setIsDraggingOver(false)
          }}
          onDrop={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsDraggingOver(false)
            onHandleFiles(
              Array.from(e.dataTransfer.files),
              visibleExistingMedia,
              setImageLimitMessage,
            )
          }}
        >
          {(visibleExistingMedia.length > 0 || newMediaFiles.length > 0) && (
            <div className="flex items-center gap-3">
              <Label>Media files</Label>
              <PlatformLimitIndicator
                length={mediaImageCount}
                metric="images"
                platforms={selectedPlatforms}
              />
            </div>
          )}
          {imageLimitMessage && (
            <p className="text-destructive text-sm">{imageLimitMessage}</p>
          )}

          {(visibleExistingMedia.length > 0 || newMediaFiles.length > 0) && (
            <div className="flex flex-col gap-3">
              {visibleExistingMedia.map((mediaFile) => (
                <MediaFileRow
                  key={mediaFile.id}
                  id={`media-file-alt-${takeKey}-${mediaFile.id}`}
                  preview={mediaFile.url!}
                  contentType={mediaFile.content_type ?? undefined}
                  altText={
                    Object.values(take.take_media_files_attributes).find(
                      (attr) => attr.id === mediaFile.id && !attr._destroy,
                    )?.alt_text ??
                    mediaFile.alt_text ??
                    ""
                  }
                  onAltTextChange={(v) =>
                    onUpdateExistingAltText(mediaFile.id, v)
                  }
                  onRemove={() => onRemoveExistingMedia(mediaFile.id)}
                  onRegenerateAltText={() =>
                    onRegenerateExistingAltText(mediaFile.id, mediaFile.url!)
                  }
                  isGeneratingAltText={generatingAltTextKeys.has(
                    existingMediaCompositeKey(takeKey, mediaFile.id),
                  )}
                />
              ))}
              {newMediaFiles.map(([mediaKey, attr]) => (
                <MediaFileRow
                  key={mediaKey}
                  id={`new-media-file-alt-${takeKey}-${mediaKey}`}
                  preview={attr.media_file!}
                  altText={attr.alt_text ?? ""}
                  onAltTextChange={(v) =>
                    onUpdateNewAltText(Number(mediaKey), v)
                  }
                  onRemove={() => onRemoveNewMedia(Number(mediaKey))}
                  onRegenerateAltText={() =>
                    onRegenerateNewAltText(Number(mediaKey))
                  }
                  isGeneratingAltText={generatingAltTextKeys.has(
                    mediaFileCompositeKey(takeKey, mediaKey),
                  )}
                />
              ))}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              onHandleFiles(
                Array.from(e.target.files ?? []),
                visibleExistingMedia,
                setImageLimitMessage,
              )
              e.target.value = ""
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-sm transition-colors ${
              isDraggingOver
                ? "border-primary bg-primary/5 text-foreground"
                : "border-border text-muted-foreground hover:border-primary/60 hover:text-foreground"
            }`}
          >
            <ImagePlus className="size-5" />
            <span>
              <span className="hidden sm:inline">
                Drag and drop, paste, or{" "}
              </span>
              <span className="text-primary font-medium underline-offset-2 hover:underline sm:no-underline">
                click to choose files
              </span>
            </span>
          </button>
        </div>
      )}
    </section>
  )
}

// Re-export the hook so story-form.tsx only needs one import
export { useTakeManagement }
